import { useState } from "react";
import {
  useGetWalletReconcileQuery,
  useWalletReconcileSellMutation,
  useHourlyAttachOcoMutation,
  useGetAdminUsersQuery,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtUsd } from "@/lib/format";
import { toast } from "sonner";

type Status = "tracked" | "partial" | "orphan" | "untracked" | "dust";

const STATUS_TONE: Record<Status, "good" | "warn" | "bad" | "muted"> = {
  tracked: "good",
  partial: "warn",
  orphan: "bad",
  untracked: "warn",
  dust: "muted",
};

const STATUS_LABEL: Record<Status, string> = {
  tracked: "tracked",
  partial: "partial (excess on exchange)",
  orphan: "orphan ($5+ untracked)",
  untracked: "untracked ($1-$5)",
  dust: "dust (<$1)",
};

export default function WalletReconcile() {
  const [uid, setUid] = useState<number>(1);
  const users = useGetAdminUsersQuery();
  const { data, isLoading, refetch } = useGetWalletReconcileQuery(
    { user_id: uid }, { pollingInterval: 30_000 },
  );
  const [sellMut, { isLoading: selling }] = useWalletReconcileSellMutation();
  const [attachOcoMut, { isLoading: attaching }] = useHourlyAttachOcoMutation();

  const sell = async (asset: string, qty: number | null) => {
    if (!confirm(`Market-sell ${qty != null ? qty.toFixed(6) : "all untracked"} ${asset}? This is irreversible.`)) return;
    try {
      const r = await sellMut({ user_id: uid, asset, qty }).unwrap() as {
        qty_sold: number; average: number; order_id: string;
      };
      toast.success(`Sold ${r.qty_sold.toFixed(4)} ${asset} @ ${r.average.toFixed(6)}`);
      refetch();
    } catch (err: unknown) {
      const detail = (err as { data?: { detail?: string } })?.data?.detail
        || "Sell failed";
      toast.error(detail);
    }
  };


  const attachOco = async (pid: number, asset: string) => {
    try {
      const r = await attachOcoMut({ pid }).unwrap();
      if (r.status === "ok") {
        toast.success(`${asset} pid ${pid}: OCO attached (list ${r.order_list_id})`);
      } else if (r.status === "skipped") {
        toast.info(`${asset} pid ${pid}: skipped — ${r.reason}`);
      } else if (r.status === "partial") {
        toast.warning(`${asset} pid ${pid}: SL-only fallback (${r.reason})`);
      } else {
        toast.error(`${asset} pid ${pid}: ${r.reason ?? r.status}`);
      }
      refetch();
    } catch (err: unknown) {
      const detail = (err as { data?: { detail?: string } })?.data?.detail
        || "Attach OCO failed";
      toast.error(detail);
    }
  };

  const totalUsd = (data?.assets ?? [])
    .reduce((acc, a) => acc + (a.usd_value ?? 0), 0);
  const untrackedUsd = (data?.assets ?? [])
    .filter((a) => a.status !== "tracked")
    .reduce((acc, a) => acc + (a.usd_value ?? 0), 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Wallet reconcile
          <span className="text-xs text-muted ml-2 font-normal">
            {data ? `${fmtUsd(totalUsd)} total · ${fmtUsd(untrackedUsd)} not tracked` : ""}
          </span>
        </CardTitle>
        <CardDescription>
          Every non-zero asset on the selected user's Binance account, with
          its tracked-position status. <b>tracked</b> = managed by one of
          hourly / dca_take_profit / grid engines. <b>partial</b> =
          some covered, excess sits free. <b>orphan</b> = real money on
          Binance with no engine managing it. <b>dust</b> = leftover &lt; $1
          from step-size rounding (usually safe to ignore).
          The "via" line under the Status badge shows WHICH engine owns
          each tracked asset.
        </CardDescription>
        <div className="mt-2 flex items-center gap-2">
          <label className="text-xs text-muted">User:</label>
          <select
            value={uid}
            onChange={(e) => setUid(parseInt(e.target.value, 10))}
            className="h-8 rounded-md border border-border bg-bg px-2 text-sm">
            {(users.data ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                #{u.id} {u.email} ({u.role})
              </option>
            ))}
          </select>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading || !data ? (
          <Skeleton className="h-32 w-full" />
        ) : data.assets.length === 0 ? (
          <p className="text-sm text-muted">No non-zero balances on this account.</p>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Asset</TH>
                <TH className="text-right">Total</TH>
                <TH className="text-right">USD value</TH>
                <TH className="text-right">Tracked qty</TH>
                <TH className="text-right">Untracked</TH>
                <TH>Status</TH>
                <TH>Positions</TH>
                <TH></TH>
              </TR>
            </THead>
            <TBody>
              {data.assets.map((a) => {
                const tone = STATUS_TONE[a.status];
                const canSell = a.status !== "tracked" && a.free > 0;
                return (
                  <TR key={a.asset}>
                    <TD className="font-mono">{a.asset}</TD>
                    <TD className="text-right tabular text-xs">
                      {a.total.toLocaleString(undefined, { maximumFractionDigits: 6 })}
                      {a.used > 0 && (
                        <div className="text-muted/70">
                          free {a.free.toFixed(4)} · locked {a.used.toFixed(4)}
                        </div>
                      )}
                    </TD>
                    <TD className="text-right tabular">
                      {a.usd_value != null ? fmtUsd(a.usd_value) : <span className="text-muted">—</span>}
                    </TD>
                    <TD className="text-right tabular text-xs">
                      {a.tracked_qty > 0 ? a.tracked_qty.toLocaleString(undefined, { maximumFractionDigits: 4 }) : <span className="text-muted">—</span>}
                    </TD>
                    <TD className="text-right tabular text-xs">
                      {a.untracked_qty > 0 ? a.untracked_qty.toLocaleString(undefined, { maximumFractionDigits: 4 }) : <span className="text-muted">—</span>}
                    </TD>
                    <TD>
                      <Badge tone={tone}>{STATUS_LABEL[a.status]}</Badge>
                      {(a as any).managed_by && (a as any).managed_by.length > 0 && (
                        <div className="text-[10px] text-muted mt-0.5">
                          via {(a as any).managed_by.join(" + ")}
                        </div>
                      )}
                      {a.unprotected_hourly_pids.length > 0 && (
                        <div className="text-xs text-bad mt-0.5">
                          ⚠ no OCO: pid {a.unprotected_hourly_pids.join(", ")}
                        </div>
                      )}
                    </TD>
                    <TD className="text-xs text-muted">
                      {a.tracked_hourly_count > 0 && <div>hourly: {a.hourly_pids.join(", ")}</div>}
                      {(a as any).tracked_dca_tp_count > 0 && (
                        <div>dca_take_profit: {(a as any).tracked_dca_tp_count} resting sell{(a as any).tracked_dca_tp_count !== 1 ? "s" : ""}</div>
                      )}
                      {(a as any).tracked_grid_count > 0 && (
                        <div>grid: {(a as any).tracked_grid_count} resting sell{(a as any).tracked_grid_count !== 1 ? "s" : ""}</div>
                      )}
                    </TD>
                    <TD>
                      <div className="flex gap-1 justify-end flex-wrap">
                        {a.unprotected_hourly_pids.length > 0 && (
                          <Button
                            size="sm" variant="ghost"
                            disabled={attaching}
                            onClick={() => attachOco(a.unprotected_hourly_pids[0], a.asset)}
                            title={
                              a.unprotected_hourly_pids.length > 1
                                ? `Multiple unprotected pids (${a.unprotected_hourly_pids.join(", ")}); attaching to the first. Click again for the next.`
                                : `Place OCO for hourly position #${a.unprotected_hourly_pids[0]}`
                            }>
                            Attach OCO
                            {a.unprotected_hourly_pids.length > 1
                              && ` (${a.unprotected_hourly_pids.length})`}
                          </Button>
                        )}
                        {canSell && (
                          <>
                            <Button
                              size="sm" variant="ghost"
                              disabled={selling}
                              onClick={() => sell(a.asset, null)}>
                              Sell
                            </Button>
                          </>
                        )}
                      </div>
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
